import React, { useState, useRef, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import SelectionCheckbox from '../../Common/SelectionCheckbox';
import { useAppStore } from '../../../GlobalStore/GlobalStore';
import { getRelativePath } from '../../../Utils/misc/pathUtils';
interface FileSearchPopupProps {}

const FileSearchPopup: React.FC<FileSearchPopupProps> = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const dynamicStateObj = useAppStore((state: any) => state.dynamicStateObj);
    const { additionalIdeContext, selectedAdditionalIdeContext } = dynamicStateObj;
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const { projectName } = environmentObj;
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const updateSelectedAdditionalIdeContext = useAppStore((state: any) => state.updateSelectedAdditionalIdeContext);
    const modalRef = useRef<HTMLDivElement>(null);

    // Updated getDisplayPath: Removed truncateFileName call
    const getDisplayPath = (filePath: string) => {
        const relativePath = getRelativePath(filePath, projectName);
        // Just return the relative path or null
        return relativePath ? relativePath : null;
    };

    const filterValidPaths = (filePaths: string[]) =>
        filePaths?.filter(filePath => {
            const displayPath = getDisplayPath(filePath);
            // Filter based on the full relative path
            return displayPath && displayPath.toLowerCase().includes(searchTerm.toLowerCase());
        }) || [];

    const filteredData = {
        files: filterValidPaths(additionalIdeContext.files),
        folders: filterValidPaths(additionalIdeContext.folders)
    };

    const isItemSelected = (item: string, type: 'files' | 'folders') => {
        return selectedAdditionalIdeContext[type]?.some((i: any) => i.filePath === item) || false;
    };

    const handleItemToggle = (item: string, type: 'files' | 'folders') => {
        const itemExists = selectedAdditionalIdeContext[type]?.some((i: any) => i.filePath === item);
        updateSelectedAdditionalIdeContext({ item, type }, itemExists ? "delete" : "add");
    };

    const handleClose = () => {
        setDynamicStateObj({ visiblePopup: null });
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
            handleClose();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="fixed inset-0 bg-neutral-100/95 dark:bg-neutral-900/95 flex items-center justify-center p-4 z-50">
            <div ref={modalRef} className="bg-neutral-300 dark:bg-neutral-800/80 rounded-xl shadow-2xl w-[95%] max-h-[80vh] overflow-hidden border border-neutral-700/50 relative">
                <div className="p-4 border-b border-neutral-300 dark:border-neutral-700">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" />
                        <input
                            type="text"
                            placeholder="Search files and folders..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="overflow-y-auto max-h-[calc(80vh-80px)]">
                    {filteredData.folders?.length > 0 && (
                        <div className="p-2">
                            <div className="text-sm font-medium text-neutral-500 dark:text-neutral-400 px-2 py-1">
                                Folders
                            </div>
                            {filteredData.folders?.map((folder: any) => (
                                <div
                                    key={folder}
                                    className="flex items-center justify-between px-4 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                                    onClick={() => handleItemToggle(folder, 'folders')}
                                >
                                    {/* Apply RTL direction to the outer span for prefix truncation */}
                                    <span className="text-sm max-w-3/4 overflow-hidden text-ellipsis whitespace-nowrap block" style={{ direction: 'rtl', textAlign: 'left' }}>
                                        {/* Inner span to maintain LTR for the actual text */}
                                        <span style={{ direction: 'ltr', display: 'inline-block' }}>
                                            {getDisplayPath(folder)}
                                        </span>
                                    </span>
                                    <SelectionCheckbox
                                        isSelected={isItemSelected(folder, 'folders')}
                                        onChange={() => handleItemToggle(folder, 'folders')}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                    {filteredData.files?.length > 0 && (
                        <div className="p-2">
                            <div className="text-sm font-medium text-neutral-500 dark:text-neutral-400 px-2 py-1">
                                Files
                            </div>
                            {filteredData.files?.map((file: any) => (
                                <div
                                    key={file}
                                    className="flex items-center justify-between px-4 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                                    onClick={() => handleItemToggle(file, 'files')}
                                >
                                    {/* Apply RTL direction to the outer span for prefix truncation */}
                                    <span className="text-sm overflow-hidden text-ellipsis whitespace-nowrap block" style={{ direction: 'rtl', textAlign: 'left' }}>
                                        {/* Inner span to maintain LTR for the actual text */}
                                        <span style={{ direction: 'ltr', display: 'inline-block' }}>
                                            {getDisplayPath(file)}
                                        </span>
                                    </span>
                                    <SelectionCheckbox
                                        isSelected={isItemSelected(file, 'files')}
                                        onChange={() => handleItemToggle(file, 'files')}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FileSearchPopup;